import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import axios from 'axios';

import { css } from '@emotion/react';
import { Dropdown, Menu, MenuProps, Popconfirm } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';

import useAuth from 'hooks/useAuth';
import { PROFILE_TYPES } from 'lib/constants';
import { useSelector } from 'store';
import { changeProfile } from 'store/actions/newPostActions';
import { NewPostProfile } from 'store/types/newPostTypes';

import { useGroupInfo } from 'components/Groups/GroupStore';
import ProfileImage from 'components/layout/Common/ProfileImage/ProfileImage';

import * as S from './ProfileSwitchStyles';

//#region TYPES
export interface ProfileSwitchProps {
  editorFocused?: boolean;
  storeKey: 'NEW_POST_GROUP';
  onProfileSwitch?: () => void;
  groupData?: any;
  origin?: 'post' | 'comment';
}

type ProfileType = 'user' | 'group';
//#endregion

const ProfileSwitch: React.VFC<ProfileSwitchProps> = ({
  editorFocused = false,
  storeKey,
  onProfileSwitch,
  groupData,
  origin,
}) => {
  const { t } = useTranslation();
  // const { userInfo, networkSettings, userPermissions } = useAuth();
  const { userInfo, networkSettings } = useAuth();
  const groupInfo = useGroupInfo();

  const dispatch = useDispatch();

  const userId = userInfo?.user?.userId;
  const userName = userInfo?.user?.name;

  const currentProfile = useSelector((state: any) => state.newPost.posts[storeKey]?.profile);

  const currentPageInfo: any = groupData || groupInfo?.page;

  const pageId = currentPageInfo?.pageId || currentPageInfo?.id || currentPageInfo?.profileId;
  const pageName = currentPageInfo?.name || currentPageInfo?.title;

  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [isProfileChosen, setProfileChosen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<ProfileType>('group');

  const [isAdmin, setIsAdmin] = useState(false);

  const participantsCanPublish = currentPageInfo?.participantsCanPublish ?? true;
  const canPublish =
    origin === 'post'
      ? participantsCanPublish && networkSettings?.allowSendingPosts
      : participantsCanPublish;

  useEffect(() => {
    let isMounted = true;

    const checkAdminStatus = async () => {
      if (!pageId) return;

      try {
        const { data } = await axios.get(`/api/profile/getPageAdmOrManager?pageId=${pageId}`);

        if (isMounted) {
          setIsAdmin(!!data);
        }
      } catch (error) {
        console.error('Erro ao verificar permissões de admin/gestor:', error);
        if (isMounted) setIsAdmin(false);
      }
    };

    checkAdminStatus();

    return () => {
      isMounted = false;
    };
  }, [pageId]);

  const userProfile: NewPostProfile = useMemo(
    () => ({
      profileId: userId,
      name: userName,
      profileType: PROFILE_TYPES.user,
      mediaId: userInfo?.user?.mediaId,
    }),
    [userId, userName, userInfo?.user?.mediaId]
  );

  const groupProfile: NewPostProfile = useMemo(
    () => ({
      profileId: pageId,
      name: pageName,
      profileType: PROFILE_TYPES.group,
      mediaId: currentPageInfo?.mediaId || groupInfo?.pageMediaId,
    }),
    [pageId, pageName, currentPageInfo?.mediaId, groupInfo?.pageMediaId]
  );

  useEffect(() => {
    if (pageId) {
      const defaultProfile = isAdmin ? groupProfile : userProfile;
      dispatch(changeProfile(storeKey, defaultProfile));
      setSelectedProfile(isAdmin ? 'group' : 'user');
      setProfileChosen(false);
    }
  }, [pageId, storeKey, isAdmin, dispatch, groupProfile, userProfile]);

  useEffect(() => {
    if (currentPageInfo && canPublish && editorFocused && !isProfileChosen) {
      setDialogVisible(true);
    }
  }, [canPublish, editorFocused, isProfileChosen, currentPageInfo]);

  if (!currentPageInfo) {
    return (
      <ProfileImage
        css={css`
          float: left;
          margin-right: 12px;
        `}
        profile={userProfile}
      />
    );
  }

  const handleMenuChange: MenuProps['onClick'] = ({ key }) => {
    const profile = key === 'group' ? groupProfile : userProfile;
    dispatch(changeProfile(storeKey, profile));
    setSelectedProfile(key as ProfileType);
    setDropdownVisible(false);
    setProfileChosen(true);
    onProfileSwitch?.();
  };

  const handleKeepProfile = () => {
    setDropdownVisible(false);
    setDialogVisible(false);
    setProfileChosen(true);
    onProfileSwitch?.();
  };

  const handleChangeProfile = () => {
    setDropdownVisible(true);
    setDialogVisible(false);
  };

  const menu = (
    <Menu onClick={handleMenuChange}>
      <Menu.ItemGroup title={<h5>{t('groups.postAs')}</h5>}>
        <S.MenuItem key="group" selected={selectedProfile === 'group'}>
          <ProfileImage className="avatar" profile={groupProfile as any} />
          {groupProfile.name}
        </S.MenuItem>
        <S.MenuItem key="user" selected={selectedProfile === 'user'}>
          <ProfileImage className="avatar" profile={userProfile as any} />
          {userProfile.name}
        </S.MenuItem>
      </Menu.ItemGroup>
    </Menu>
  );

  return (
    <>
      {isAdmin ? (
        <Popconfirm
          cancelText={t('new-post.profileConfirmCancel')}
          okButtonProps={{ style: { fontWeight: 600 } }}
          okText={t('new-post.profileConfirmOk')}
          onCancel={handleKeepProfile}
          onConfirm={handleChangeProfile}
          placement="bottom"
          title={
            <Trans i18nKey="new-post.profileConfirm">
              Você está postando como:{' '}
              <strong>{{ name: currentProfile?.name || groupProfile.name }}</strong>
              <br />
              isso está certo?
            </Trans>
          }
          visible={dialogVisible}
          disabled={!canPublish}
        >
          <Dropdown
            onVisibleChange={setDropdownVisible}
            overlay={menu}
            placement="bottomLeft"
            trigger={['click']}
            visible={dropdownVisible}
            disabled={!canPublish}
          >
            <div
              css={css`
                float: left;
                margin-right: 12px;
                cursor: pointer;
              `}
            >
              {currentProfile ? (
                <ProfileImage
                  css={css`
                    margin-right: 4px;
                  `}
                  profile={
                    currentProfile.profileType === PROFILE_TYPES.group
                      ? (groupProfile as any)
                      : (userProfile as any)
                  }
                />
              ) : (
                <ProfileImage
                  css={css`
                    margin-right: 4px;
                  `}
                  profile={groupProfile as any}
                />
              )}
              {canPublish && <FontAwesomeIcon icon={faCaretDown} size="lg" />}
            </div>
          </Dropdown>
        </Popconfirm>
      ) : (
        <ProfileImage
          css={css`
            float: left;
            margin-right: 12px;
          `}
          profile={userProfile as any}
        />
      )}
    </>
  );
};

export default ProfileSwitch;
